<script lang="ts" setup>
import type { Placement } from '../../ideo/ui-popover/types';
import { resolveConfirmationTeleportTarget, type ConfirmationTeleportTarget } from './teleportTarget';

defineOptions({
    name: 'Confirmation',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    value: any;
    target: any;
    message: string;
    placement?: Placement;
    id?: string | null;
    wrapperClass?: Record<string, boolean> | string[] | string;
    actionClass?: Record<string, boolean> | string[] | string;
    tone?: 'danger' | 'neutral';
    icon?: string;
}>(), {
    placement: 'auto',
    wrapperClass: '',
    actionClass: '',
    id: null,
    tone: 'danger',
    icon: undefined
});

const emit = defineEmits<{
    (e: 'confirm', value: any): void;
    (e: 'cancel'): void;
}>();

const popover = ref<any>(null);
const teleportTo = ref<ConfirmationTeleportTarget>('body');

async function syncTeleportTarget()
{
    await nextTick();

    teleportTo.value = resolveConfirmationTeleportTarget(props.target);
}

function open(target?: HTMLElement): void
{
    popover.value.open(target);
}

function close(): void
{
    popover.value.close();
}

function confirm(): void
{
    close();
    emit('confirm', props.value);
}

function cancel(): void
{
    close();
    emit('cancel');
}

const wrapperClasses = computed(() => ({
    'confirmation-content': true,
    ...normalizeClasses(props.wrapperClass)
}));

const actionClasses = computed(() => ({
    'confirmation-actions': true,
    ...normalizeClasses(props.actionClass)
}));

defineExpose({
    open
});

onMounted(syncTeleportTarget);

watch(() => props.target, syncTeleportTarget);
</script>

<template>
    <Teleport :to="teleportTo">
        <IdeoPopover
            v-bind="$attrs"
            :id="id"
            ref="popover"
            class="confirmation-popover"
            :target="target"
            :placement="placement"
            triggers="click"
            variant="menu"
            :arrow="false"
            :body="false"
            :close-on-global-open="false"
            role="dialog"
            :aria-label="$t('[[[Potwierdzenie]]]')"
            @pointerdown.stop
            @mousedown.stop
            @mouseup.stop
            @click.stop
        >
            <div :class="[wrapperClasses, 'flex min-h-[32px] w-fit min-w-[202px] max-w-[min(352px,calc(100vw-56px))] items-center gap-[8px] ps-[6px]']">
                <IdeoIcon :name="props.icon || (props.tone === 'danger' ? 'trash-2' : 'info')" :size="13" class="shrink-0" :class="props.tone === 'danger' ? 'text-danger dark:text-danger-dark' : 'text-fg dark:text-fg-dark'" />
                <div class="confirmation-message min-w-[18ch] flex-1 py-[6px] text-start text-[0.8125rem]/normal font-[400] [overflow-wrap:break-word]" :class="props.tone === 'danger' ? 'text-danger dark:text-danger-dark' : 'text-fg dark:text-fg-dark'" v-html="message.replaceAll('\n', '<br>')" />
                <IdeoJoinedActionGroup :class="[actionClasses, 'ms-auto flex-none justify-end']">
                    <IdeoAction :aria-label="$t('[[[Anuluj]]]')" :title="$t('[[[Anuluj]]]')" variant="secondary" size="xs" square icon="x" @click.stop="cancel" />
                    <IdeoAction :aria-label="$t('[[[Potwierdź]]]')" :title="$t('[[[Potwierdź]]]')" :variant="props.tone === 'danger' ? 'danger' : 'primary'" size="xs" square icon="check" @click.stop="confirm" />
                </IdeoJoinedActionGroup>
            </div>
        </IdeoPopover>
    </Teleport>
</template>
